/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['checkBox', 'listOptions'] }] */
import { Component } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { err } from 'actions/errors';
import { sendNotificationSubscriptions } from 'actions/productdetail/sharing';
import { INTERNET_EMAIL } from 'common/regex';
import NotifyNewStylesImage from 'images/notify_new_styles.svg';
import modalStyles from 'styles/components/common/modal.scss';
import css from 'styles/components/productdetail/productNotify.scss';
import brandSpecificCss from 'styles/components/productdetail/brandNotify.scss';

const regex = INTERNET_EMAIL.toString().slice(2, -2);
const initialState = { emailAddressValid: true, hasSubmittedInvalid: false };
const DONE_MESSAGE = <p role="alert">You have been added to the list! We will notify you when we get new products in stock for this brand.</p>;

const invalidMsg = <div className={cn(brandSpecificCss.emailValidationError, css.error)} id="errorMsg">Oops, <strong>enter your email</strong> so we can contact you when we get new styles in stock.</div>;

export class BrandStylesNotifyModal extends Component {
  state = initialState;

  close = () => {
    this.props.onClose();
    this.setState(initialState);
  }

  makeMessageContent(isSuccess) {
    const { testId } = this.context;
    return (
      <div>
        <div className={cn(brandSpecificCss.brandMessageContent, css.messageContent)}>
          <div className={cn({ [css.complete]: isSuccess, [css.error]: !isSuccess })} data-test-id={testId('notifyDoneMessage')}>{isSuccess ? DONE_MESSAGE : err.ERROR_ADDING_TO_EMAIL_LIST}</div>
          <img src={NotifyNewStylesImage} alt="Notify for new styles" className={css.imageFlair}/>
        </div>
        <div className={css.actionBar}>
          <button
            type="button"
            className={css.sendButton}
            data-test-id={testId('notifyClose')}
            onClick={this.close}
          >Close</button>
        </div>
      </div>
    );
  }

  makeModalContent() {
    const { productNotify: { sending, successfullySent }, brandId, brandName } = this.props;
    const { testId } = this.context;
    const { emailAddressValid, hasSubmittedInvalid } = this.state;
    if (typeof successfullySent === 'boolean') {
      return this.makeMessageContent(successfullySent);
    }
    return (
      <div>
        <form
          method="post"
          onReset={this.close}
          onSubmit={this.send}
          noValidate>
          <div className={css.productNotifyContainer}>
            <div>Simply sign up and we'll notify you when we add new styles of {brandName} to Zappos!</div>
            <label htmlFor="productNotifyEmailAddress" className={cn(brandSpecificCss.emailLabel, { [css.labelInvalid]: !emailAddressValid })}>Your Email</label>
            <input
              type="email"
              id="productNotifyEmailAddress"
              className={cn(css.emailAddress, { [css.invalid]: !emailAddressValid })}
              name="emailAddress"
              pattern={regex}
              required
              data-test-id={testId('notifyEmail')}
              aria-describedby="errorMsg"
              onBlur={this.validateEmail}
            />
            {hasSubmittedInvalid && invalidMsg}
            <div>Zappos.com respects your <Link to="/c/privacy-policy">privacy</Link>. We don't rent or sell your personal information to anyone.</div>
            <img src={NotifyNewStylesImage} alt="Notify of new styles" className={css.imageFlair}/>
            <input type="hidden" name="brandId" value={brandId} />
          </div>
          <div className={css.actionBar}>
            <button
              type="reset"
              className={css.closeButton}
              disabled={sending}
              data-test-id={testId('cancelButton')}>Cancel</button>
            <button
              type="submit"
              className={css.sendButton}
              disabled={sending}
              data-test-id={testId('notifyButton')}>{sending ? 'Working..' : 'notify me of new styles'}</button>
          </div>
        </form>
      </div>
    );
  }

  send = e => {
    const { brandId, sendNotificationSubscriptions } = this.props;
    const { emailAddress } = e.currentTarget;

    e.preventDefault();
    // we test this on the fly here rather than relying on state because if the user types gibberish and hits enter, the onblur event doesnt fire before submit.
    if (emailAddress && emailAddress.checkValidity()) {
      sendNotificationSubscriptions(emailAddress.value, null, null, brandId);
    } else {
      this.setState({ hasSubmittedInvalid: true });
      emailAddress.focus();
    }

  }

  validateEmail = ({ currentTarget }) => {
    this.setState({ emailAddressValid: currentTarget.checkValidity() });
  }

  render() {
    const { isOpen } = this.props;
    const { testId } = this.context;
    return (
      <Modal
        isOpen={isOpen}
        overlayClassName={modalStyles.modalOverlay}
        className={cn(modalStyles.modalContent, css.modalContent)}
        contentLabel="Notify for new brand styles Popup"
        bodyOpenClassName={css.modalOpenBodyStyle}>
        <div data-test-id={testId('notifyMeBrandStyles')}>
          <div className={modalStyles.header}>
            <h2 className={brandSpecificCss.brandHeader}>Sign Up to Receive Updates for New Styles from This Brand!</h2>
            <button
              type="button"
              className={modalStyles.close}
              onClick={this.close}
              aria-label="Close"/>
          </div>
          {this.makeModalContent()}
        </div>
      </Modal>
    );
  }
}

BrandStylesNotifyModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  productNotify: PropTypes.object.isRequired,
  brandId: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired
};

BrandStylesNotifyModal.contextTypes = {
  testId: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    productNotify: state.sharing.productNotify
  };
}

export default connect(mapStateToProps, { sendNotificationSubscriptions })(BrandStylesNotifyModal);
